/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useQueryParam, ArrayParam } from 'use-query-params';
import { Typeahead } from 'react-bootstrap-typeahead';
import DealerStatusList from '../../components/DealerStatusList';
import { DEFAULT_DEALER_FEATURE_LIST } from '../../util/constants';
import { useDateRange } from '../../util/useDateRange';
import './DealerStatus.scss';

/**
 * Handles the list of selected feature
 */
function useFeatureList() {
  const [queryFeatureList, setFeatureList] = useQueryParam(
    'featureList',
    ArrayParam,
  );
  const featureList = queryFeatureList || DEFAULT_DEALER_FEATURE_LIST;

  /**
   * Updates the selected feature at the given index
   */
  const onFeatureChange = (index, newFeature) => {
    const newFeatureList = [...featureList];
    newFeatureList[index] = newFeature;
    setFeatureList(newFeatureList);
  };

  return [featureList, onFeatureChange];
}

/**
 * Handles filtering the provided dealer list based on the user's selection
 *
 * @param dealers the list of available dealers
 */
function useFilteredDealersList(dealers) {
  // this is a list of dealer ids
  const [queryFilteredDealers, setFilteredDealers] = useQueryParam(
    'filteredDealers',
    ArrayParam,
  );
  const filteredDealers = (queryFilteredDealers || [])
    .map(filteredId => (dealers || []).find(dealer => dealer.id === filteredId))
    .filter(dealer => !!dealer);

  const picker = (
    <Typeahead
      id="dealer-typeahead"
      placeholder="Filter by Dealer Name"
      className="dealer-typeahead"
      labelKey="name"
      multiple
      selectHintOnEnter
      clearButton
      options={dealers}
      selected={filteredDealers}
      onChange={selected =>
        setFilteredDealers(selected.map(selectedDealer => selectedDealer.id))
      }
    />
  );

  // return all the dealers if none are currently selected
  return [filteredDealers.length !== 0 ? filteredDealers : dealers, picker];
}

/**
 * Handles the list of expanded dealers
 */
function useExpandedDealersList() {
  // this is a list of dealer ids
  const [queryExpandedDealers, setExpandedDealers] = useQueryParam(
    'expandedDealers',
    ArrayParam,
  );
  const expandedDealers = queryExpandedDealers || [];

  /**
   * Toggles the provided dealer in the expanded dealer list
   */
  const onDealerExpand = dealerId => {
    let newExpandedDealers;
    if (expandedDealers.includes(dealerId)) {
      // remove if it exists in the list
      newExpandedDealers = expandedDealers.filter(id => id !== dealerId);
    } else {
      // add is it doesn't exist in the list
      newExpandedDealers = expandedDealers.concat(dealerId);
    }
    setExpandedDealers(newExpandedDealers);
  };

  return [expandedDealers, onDealerExpand];
}

function DealerStatus(props) {
  const [featureList, onFeatureChange] = useFeatureList();
  const [filteredDealers, dealerPicker] = useFilteredDealersList(
    props.dealers.dealers,
  );
  const [expandedDealers, onDealerExpand] = useExpandedDealersList();
  const [
    startDate,
    endDate,
    datePicker,
    rangeLabel,
    fullRangeLabel,
  ] = useDateRange();

  // query the list of dealers at start
  useEffect(() => {
    props.actions.dealersRequest({
      addExtraInformation: false,
      extraAttributes: ['featureConfig', 'desking'],
    });
  }, []);

  // keep track for whether a fetch is currently active
  const [fetching, setFetching] = useState(false);
  useEffect(() => {
    setFetching(
      props.dealers.fetching || props.analytics.featureMetrics.fetching,
    );
  }, [props.dealers.fetching, props.analytics.featureMetrics.fetching]);

  // keep track of the parameters used in the last query
  const queriedStartDate = useRef();
  const queriedEndDate = useRef();
  const queriedExpandedDealers = useRef();
  // requery the feature metrics whenever new the expanded dealer list or date changes
  useEffect(() => {
    if (!expandedDealers || !expandedDealers.length || !startDate || !endDate) {
      return;
    }
    // don't do a requery if the date hasn't changed and the expanded dealer list is a subset of the previous one
    if (
      queriedStartDate.current === startDate &&
      queriedEndDate.current === endDate
    ) {
      const isSubset = expandedDealers.every(dealerId =>
        (queriedExpandedDealers.current || []).includes(dealerId),
      );
      if (isSubset) {
        return;
      }
    }
    props.actions.analyticsFeatureMetricsRequest({
      dealerIds: expandedDealers,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    setFetching(true);
    // save the params used in the query
    queriedStartDate.current = startDate;
    queriedEndDate.current = endDate;
    queriedExpandedDealers.current = expandedDealers;
  }, [
    expandedDealers.join(','),
    startDate.toISOString(),
    endDate.toISOString(),
  ]);

  return (
    <div>
      <div className="dealer-status-header">
        <h3>
          <strong>{filteredDealers.length}</strong> Dealers from{' '}
          <strong>{rangeLabel}</strong>
          {datePicker}
          <span className="display-range-label">{fullRangeLabel}</span>
        </h3>
      </div>
      {dealerPicker}
      <br />
      <DealerStatusList
        dealers={filteredDealers}
        featureList={featureList}
        featureMetrics={props.analytics.featureMetrics.data}
        onFeatureChange={onFeatureChange}
        expandedDealers={expandedDealers}
        onDealerExpand={onDealerExpand}
        error={props.dealers.error || props.analytics.featureMetrics.error}
        fetching={fetching}
      />
    </div>
  );
}

DealerStatus.propTypes = {
  dealers: PropTypes.object,
  analytics: PropTypes.object,
  actions: PropTypes.object,
};

export default DealerStatus;
